import { CSSProperties } from "react";
import "./GiftBoxCard.css";

/**
 * Gift box card. Everything it shows comes from props, so it works for any campaign.
 *
 * <GiftBoxCard
 *   number={1}
 *   image="/eid55/boxes/box-1.jpg"
 *   color="#F4D9CF"
 *   accent="#C25441"
 *   title="City streets"
 *   description="Scarf + Mug + UAE flag pin"
 *   ctaLabel="Order this box"
 *   onSelect={() => …}
 * />
 */

export interface GiftBoxCardProps {
  /** shown as 01, 02 … on the photo and next to the color square */
  number: number;
  image: string;
  /** card background */
  color: string;
  /** the small color square; defaults to `color` */
  accent?: string;
  title: string;
  description?: string;
  /** alt text for the photo; defaults to the title */
  alt?: string;
  ctaLabel: string;
  onSelect: () => void;
  /** highlights the card (e.g. the box currently in the order form) */
  selected?: boolean;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

export default function GiftBoxCard({
  number,
  image,
  color,
  accent,
  title,
  description,
  alt,
  ctaLabel,
  onSelect,
  selected = false,
  className = "",
}: GiftBoxCardProps) {
  const style = {
    ["--gbc-color" as string]: color,
    ["--gbc-accent" as string]: accent ?? color,
  } as CSSProperties;

  return (
    <article className={`gbc${selected ? " gbc--selected" : ""} ${className}`.trim()} style={style}>
      <div className="gbc__pic">
        <span className="gbc__num">{pad(number)}</span>
        <img src={image} alt={alt ?? title} loading="lazy" />
      </div>
      <div className="gbc__meta">
        <h3 className="gbc__title">{title}</h3>
        {description && <p className="gbc__desc">{description}</p>}
        <div className="gbc__row">
          <span className="gbc__swatch">
            <i aria-hidden="true" />#{pad(number)}
          </span>
          <button className="gbc__cta" type="button" onClick={onSelect} aria-pressed={selected}>
            {ctaLabel}
          </button>
        </div>
      </div>
    </article>
  );
}
